import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, Package, Save, Search } from 'lucide-react';
import { Producto, Supplier } from '../../types';
import EditableCell from './EditableCell';
import { apiService } from '../../services/api';
import { useDeviceDetect } from '../../hooks/useDeviceDetect';

interface ProductTableProps {
  productos: Producto[];
  onEditProduct: (product: Producto) => Promise<void>;
}

const ProductTable: React.FC<ProductTableProps> = ({ productos, onEditProduct }) => {
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set());
  const [editedProducts, setEditedProducts] = useState<{[key: string]: Producto}>({});
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [savingProducts, setSavingProducts] = useState<Set<string>>(new Set());
  const { isTablet, isMobile } = useDeviceDetect();

  useEffect(() => {
    // Fallback suppliers from the migration
    const fallbackSuppliers: Supplier[] = [
      { _id: '1', nombre: 'Ash y Maryl', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '2', nombre: 'Ash/Maryl Gastos', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '3', nombre: 'Ashok y Maryl/Dympco', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '4', nombre: 'Bolpack', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '5', nombre: 'Discount Coffee', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '6', nombre: 'Grano x Grano (Daniel/Javier)', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '7', nombre: 'Grano x Grano Daniel/Javier', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '8', nombre: 'Panaderia/Isa', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '9', nombre: 'Retto', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
      { _id: '10', nombre: 'Soy té', activo: true, fechaCreacion: '', ultimaActualizacion: '' },
    ];
    const fetchSuppliers = async () => {
      try {
        console.log('Fetching suppliers...');
        const activeSuppliers = await apiService.getActiveSuppliers();
        console.log('Active suppliers fetched:', activeSuppliers);
        setSuppliers(activeSuppliers);
      } catch (error) {
        console.error('Error cargando proveedores:', error);
        // Try to fetch all suppliers if active fails
        try {
          const allSuppliers = await apiService.getAllSuppliers();
          console.log('All suppliers fetched:', allSuppliers);
          setSuppliers(allSuppliers.filter(s => s.activo !== false));
        } catch (error2) {
          console.error('Error cargando todos los proveedores:', error2);
          // Use fallback suppliers
          console.log('Using fallback suppliers');
          setSuppliers(fallbackSuppliers);
        }
      }
    };
    
    fetchSuppliers();
  }, []);

  const getStockStatus = (producto: Producto) => {
    const { stockFinal, stockMinimo } = producto;
    
    // Stock bajo (menos que el mínimo)
    if (stockFinal < stockMinimo) {
      return { status: 'low', color: 'text-red-600', icon: AlertTriangle };
    }
    
    // Stock cerca del mínimo (entre mínimo y mínimo + 3)
    if (stockFinal <= stockMinimo + 3) {
      return { status: 'warning', color: 'text-yellow-600', icon: Calendar };
    }
    
    // Stock bueno (más de mínimo + 3)
    return { status: 'good', color: 'text-green-600', icon: Package };
  };

  const handleCellEdit = (productId: string, field: keyof Producto, value: string | number) => {
    const product = productos.find(p => p.id === productId);
    if (!product) return;

    const updatedProduct = {
      ...product,
      [field]: value
    };

    setEditedProducts(prev => ({
      ...prev,
      [productId]: updatedProduct
    }));
    setEditingRows(prev => {
      const newSet = new Set(prev);
      newSet.add(productId);
      return newSet;
    });
  };

  const handleSaveProduct = async (productId: string) => {
    const editedProduct = editedProducts[productId];
    if (editedProduct) {
      // Add to saving set
      setSavingProducts(prev => new Set(prev).add(productId));
      
      try {
        // Find the original product to compare changes
        const originalProduct = productos.find(p => p.id === productId);
        if (originalProduct) {
          // Create an object with only the changed fields
          const changes: Partial<Producto> = {};
          Object.keys(editedProduct).forEach(key => {
            const k = key as keyof Producto;
            if (editedProduct[k] !== originalProduct[k]) {
              changes[k] = editedProduct[k] as any;
            }
          });
          
          // If there are changes, save them
          if (Object.keys(changes).length > 0) {
            console.log('Saving changes for', editedProduct.producto, ':', changes);
            await onEditProduct(editedProduct);
          }
        } else {
          // Fallback to saving entire product if original not found
          await onEditProduct(editedProduct);
        }
        
        // Remove from editing state after successful save
        setEditingRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        setEditedProducts(prev => {
          const newEdited = { ...prev };
          delete newEdited[productId];
          return newEdited;
        });
      } catch (error) {
        console.error('Error saving product:', error);
        alert('Error al guardar el producto. Por favor, intente de nuevo.');
      } finally {
        // Remove from saving set
        setSavingProducts(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      }
    }
  };

  const getCategorias = () => {
    const categorias = new Set(productos.map(p => p.categoria));
    return Array.from(categorias).sort();
  };

  // Get unique suppliers from products
  const getProveedores = () => {
    const proveedores = new Set(productos.map(p => p.proveedor));
    return Array.from(proveedores).sort();
  };

  // Filter products based on search term, category, and supplier
  const filteredProducts = productos.filter(producto => {
    const matchesSearch = producto.producto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         producto.proveedor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || producto.categoria === selectedCategory;
    const matchesSupplier = selectedSupplier === 'all' || producto.proveedor === selectedSupplier;
    return matchesSearch && matchesCategory && matchesSupplier;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-gray/20">
      <div className="px-4 md:px-6 py-4 border-b border-light-gray/20 rounded-t-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-dark-text font-heading">
            Inventario de Productos
          </h3>
          <div className="text-sm text-medium-gray">
            Total: {filteredProducts.length} productos
          </div>
        </div>
        
        {/* Search and filter section */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          {/* Search input */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray" size={20} />
              <input
                type="text"
                placeholder="Buscar producto o proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              />
            </div>
          </div>
          
          {/* Category filter */}
          <div className="sm:w-64">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-white"
            >
              <option value="all">Todas las categorías</option>
              {getCategorias().map(categoria => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>
          </div>
          
          {/* Supplier filter */}
          <div className="sm:w-64">
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="w-full px-4 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-white"
            >
              <option value="all">Todos los proveedores</option>
              {getProveedores().map(proveedor => (
                <option key={proveedor} value={proveedor}>
                  {proveedor}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Desktop view */}
      {!isTablet && !isMobile && (
      <div className="overflow-x-auto max-h-[calc(100vh-200px)] overflow-y-auto rounded-b-lg pb-20">
        <table className="min-w-full divide-y divide-light-gray/20">
          <thead className="bg-cream">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                Categoría
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                Producto
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                Unidad
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                Proveedor
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-medium-gray uppercase tracking-wider">
                Stock
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-medium-gray uppercase tracking-wider">
                Mín/Máx
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-medium-gray uppercase tracking-wider">
                Fecha Cad.
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-medium-gray uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-medium-gray uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-light-gray/20">
            {getCategorias().filter(categoria => 
              filteredProducts.some(p => p.categoria === categoria)
            ).map(categoria => {
              const productosCategoria = filteredProducts.filter(p => p.categoria === categoria);
              
              return (
                <React.Fragment key={categoria}>
                  <tr className="bg-cream/50">
                    <td colSpan={9} className="px-4 py-2 text-sm font-semibold text-warm-brown">
                      {categoria}
                    </td>
                  </tr>
                  {productosCategoria.map((producto) => {
                    const isEditing = editingRows.has(producto.id);
                    const currentProduct = editedProducts[producto.id] || producto;
                    const stockStatus = getStockStatus(currentProduct);
                    const StatusIcon = stockStatus.icon;

                    return (
                      <tr key={producto.id} className={`hover:bg-cream/30 transition-colors ${isEditing ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-3 text-sm text-medium-gray">
                          {/* Categoría ya se muestra como encabezado */}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <EditableCell
                            value={currentProduct.producto}
                            type="text"
                            onSave={(value) => handleCellEdit(producto.id, 'producto', value)}
                            className="text-sm font-medium text-dark-text"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <EditableCell
                            value={currentProduct.unidad}
                            type="text"
                            onSave={(value) => handleCellEdit(producto.id, 'unidad', value)}
                            className="text-dark-text"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <EditableCell
                            value={currentProduct.proveedor}
                            type="select"
                            options={suppliers.map(s => ({ value: s.nombre, label: s.nombre }))}
                            onSave={(value) => handleCellEdit(producto.id, 'proveedor', value)}
                            className="text-dark-text"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                          <EditableCell
                            value={currentProduct.stockFinal}
                            type="number"
                            onSave={(value) => handleCellEdit(producto.id, 'stockFinal', value)}
                            className={currentProduct.stockFinal <= currentProduct.stockMinimo ? 'text-red-600 font-semibold' : ''}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <EditableCell
                              value={currentProduct.stockMinimo}
                              type="number"
                              onSave={(value) => handleCellEdit(producto.id, 'stockMinimo', value)}
                              className="text-yellow-600"
                            />
                            <span className="text-medium-gray">/</span>
                            <EditableCell
                              value={currentProduct.stockMaximo}
                              type="number"
                              onSave={(value) => handleCellEdit(producto.id, 'stockMaximo', value)}
                              className="text-green-600"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                          <EditableCell
                            value={currentProduct.fechaCaducidad || ''}
                            type="date"
                            onSave={(value) => handleCellEdit(producto.id, 'fechaCaducidad', value)}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className={`flex items-center justify-center ${stockStatus.color}`}>
                            <StatusIcon size={16} className="mr-1" />
                            <span className="text-xs font-medium">
                              {stockStatus.status === 'low' && 'Bajo'}
                              {stockStatus.status === 'warning' && 'Alerta'}
                              {stockStatus.status === 'good' && 'Bueno'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {isEditing ? (
                            <button
                              onClick={() => handleSaveProduct(producto.id)}
                              disabled={savingProducts.has(producto.id)}
                              className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Guardar cambios"
                            >
                              {savingProducts.has(producto.id) ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                              ) : (
                                <Save size={20} />
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-medium-gray">Editar celdas</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      
      {/* Mobile/Tablet view */}
      {(isTablet || isMobile) && (
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto rounded-b-lg pb-20">
        {getCategorias().filter(categoria => 
          filteredProducts.some(p => p.categoria === categoria)
        ).map(categoria => {
          const productosCategoria = filteredProducts.filter(p => p.categoria === categoria);
          
          return (
            <div key={categoria} className="border-b border-light-gray/20">
              <div className="px-4 py-3 bg-cream/50">
                <h4 className="text-sm font-semibold text-warm-brown">{categoria}</h4>
              </div>
              <div className="divide-y divide-light-gray/20">
                {productosCategoria.map((producto) => {
                  const isEditing = editingRows.has(producto.id);
                  const currentProduct = editedProducts[producto.id] || producto;
                  const stockStatus = getStockStatus(currentProduct);
                  const StatusIcon = stockStatus.icon;

                  return (
                    <div key={producto.id} className={`p-4 ${isEditing ? 'bg-yellow-50' : ''}`}>
                      <div className="space-y-4">
                        {/* Top section: Product info and status */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-dark-text">
                              {currentProduct.producto}
                            </h4>
                            <div className="flex items-center gap-4 mt-1 text-sm text-medium-gray">
                              <span>{currentProduct.unidad}</span>
                              <span>•</span>
                              <span>{currentProduct.proveedor}</span>
                            </div>
                          </div>
                          <div className={`flex items-center ${stockStatus.color} bg-white px-3 py-1 rounded-lg border ${
                            stockStatus.status === 'low' ? 'border-red-200' : 
                            stockStatus.status === 'warning' ? 'border-yellow-200' : 
                            'border-green-200'
                          }`}>
                            <StatusIcon size={18} className="mr-1" />
                            <span className="text-sm font-medium">
                              {stockStatus.status === 'low' && 'Stock Bajo'}
                              {stockStatus.status === 'warning' && 'Alerta'}
                              {stockStatus.status === 'good' && 'OK'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Bottom section: Interactive number inputs */}
                        <div className="grid grid-cols-4 gap-3">
                          {/* Stock Actual */}
                          <div className="text-center">
                            <label className="text-xs text-medium-gray uppercase block mb-1">Actual</label>
                            <div className="border-2 border-warm-brown/30 rounded-lg p-3 hover:border-warm-brown transition-colors">
                              <EditableCell
                                value={currentProduct.stockFinal}
                                type="number"
                                onSave={(value) => handleCellEdit(producto.id, 'stockFinal', value)}
                                className={`text-2xl font-bold ${currentProduct.stockFinal <= currentProduct.stockMinimo ? 'text-red-600' : 'text-dark-text'}`}
                              />
                            </div>
                          </div>
                          
                          {/* Stock Mínimo */}
                          <div className="text-center">
                            <label className="text-xs text-medium-gray uppercase block mb-1">Mínimo</label>
                            <div className="border-2 border-yellow-300 rounded-lg p-3 hover:border-yellow-500 transition-colors">
                              <EditableCell
                                value={currentProduct.stockMinimo}
                                type="number"
                                onSave={(value) => handleCellEdit(producto.id, 'stockMinimo', value)}
                                className="text-2xl font-bold text-yellow-600"
                              />
                            </div>
                          </div>
                          
                          {/* Stock Máximo */}
                          <div className="text-center">
                            <label className="text-xs text-medium-gray uppercase block mb-1">Máximo</label>
                            <div className="border-2 border-green-300 rounded-lg p-3 hover:border-green-500 transition-colors">
                              <EditableCell
                                value={currentProduct.stockMaximo}
                                type="number"
                                onSave={(value) => handleCellEdit(producto.id, 'stockMaximo', value)}
                                className="text-2xl font-bold text-green-600"
                              />
                            </div>
                          </div>
                          
                          {/* Save button or empty space */}
                          <div className="text-center">
                            {isEditing ? (
                              <div>
                                <label className="text-xs text-medium-gray uppercase block mb-1">Guardar</label>
                                <button
                                  onClick={() => handleSaveProduct(producto.id)}
                                  disabled={savingProducts.has(producto.id)}
                                  className="w-full border-2 border-green-600 bg-green-600 text-white rounded-lg p-3 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                  title="Guardar cambios"
                                >
                                  {savingProducts.has(producto.id) ? (
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                  ) : (
                                    <Save size={24} />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div>
                                <label className="text-xs text-medium-gray uppercase block mb-1">&nbsp;</label>
                                <div className="h-[72px]"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default ProductTable;